import { z } from 'zod';
import type { AgentDefinitionResponse } from '@erp/rpc';

const OrchestrationConfigSchema = z.object({
  taskManagement: z
    .object({
      enabled: z.boolean().optional(),
      startConditions: z.array(z.unknown()).optional(),
    })
    .optional(),
});

/**
 * An agent is "orchestrated" when task management is enabled — its runs are
 * driven by the task-loop coordinator on its own schedule rather than started
 * by hand. Shared by the monitor header (which disables manual Start for these
 * agents) and the org-configured agent page (which shows the task queue only
 * for them).
 */
export function isOrchestratedAgent(
  config: AgentDefinitionResponse['config'],
): boolean {
  const parsed = OrchestrationConfigSchema.safeParse(config);
  return parsed.success && parsed.data.taskManagement?.enabled === true;
}

/**
 * Whether the agent's task management has at least one start-condition group.
 * An orchestrated agent without any never starts: the coordinator refuses it
 * with `no_start_conditions_configured`.
 */
export function hasStartConditions(
  config: AgentDefinitionResponse['config'],
): boolean {
  const parsed = OrchestrationConfigSchema.safeParse(config);
  return (
    parsed.success &&
    (parsed.data.taskManagement?.startConditions?.length ?? 0) > 0
  );
}
